import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { notifyClient } from "@/lib/notify";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Zap, Truck, Package, Route,
  Trophy, Phone, Mail, Clock, ArrowLeft, AlertTriangle,
  TrendingDown, TrendingUp, Minus, CircleDot, CheckCircle2,
  Radio, Shield, BadgeCheck, StickyNote, FileText,
  MessageSquare, ClipboardList, Navigation2,
} from "lucide-react";
import { format, differenceInCalendarDays, differenceInSeconds } from "date-fns";
import { formatPhoneNumber } from "@/lib/formatPhone";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAvatarUrl } from "@/lib/avatars/useAvatarUrl";
import { MarketInsightsPanel } from "@/components/lr-direct/MarketInsightsPanel";
import { AtlasAwardAnalysisPanel } from "@/components/lr-direct/AtlasAwardAnalysisPanel";
import { TransitTimePredictorWidget } from "@/components/lr-direct/TransitTimePredictorWidget";
import { useResolvedLoadMiles } from "@/hooks/useResolvedLoadMiles";
import { useChatSystem } from "@/hooks/useChatSystem";
import { displayCompanyName } from "@/lib/displayCompanyName";


// LR Direct is quote-only for now. Flip to true to re-enable the Live Load Room award flow.
const AWARDS_ENABLED = true;



/* ── Types ── */
interface DirectLoad {
  id: string;
  load_number: string;
  origin_name?: string | null;
  origin_address?: string | null;
  origin_city: string;
  origin_state: string;
  origin_postal_code?: string | null;
  destination_name?: string | null;
  destination_address?: string | null;
  destination_city: string;
  destination_state: string;
  destination_postal_code?: string | null;
  equipment_type: string;
  pickup_date: string;
  delivery_date: string | null;
  commodity: string | null;
  weight_lbs: number | null;
  length_ft: number | null;
  total_miles: number | null;
  load_type: string | null;
  bid_deadline: string | null;
  max_bidders: number;
  priority_tag: string;
  status: string;
  target_rate_min: number | null;
  target_rate_max: number | null;
  created_by: string;
  awarded_bid_id: string | null;
  reference_number?: string | null;
  special_instructions?: string | null;
  shipper_notes_to_brokers?: string | null;
  appointment_type?: string | null;
  handling_requirements?: string[] | null;
  origin_latitude?: number | null;
  origin_longitude?: number | null;
  destination_latitude?: number | null;
  destination_longitude?: number | null;
}


interface DirectStop {
  id: string;
  stop_number: number;
  stop_type: string;
  name?: string | null;
  address?: string | null;
  city: string;
  state: string;
  postal_code?: string | null;
  scheduled_date: string;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  instructions?: string | null;
}

interface LiveBid {
  id: string;
  bid_amount: number;
  message: string | null;
  status: string;
  submitted_at: string;
  broker_id: string;
  broker_company_id: string | null;
  broker_company_name: string;
  broker_company_verified?: boolean;
  broker_avatar_path?: string | null;
  ready_to_cover?: boolean;
  truck_in_area?: boolean;
  team_available?: boolean;
  eta_hours?: number | null;
  rate_type?: string | null;
  isNew?: boolean;
}

interface AwardContact {
  company_name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
}

/* ── Helpers ── */
function getPickupChip(pickupDate: string) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const pickup = new Date(pickupDate + "T00:00:00");
  const diff = differenceInCalendarDays(pickup, today);
  if (diff <= 0) return { label: "Today", urgent: true };
  if (diff === 1) return { label: "Tomorrow", urgent: true };
  if (diff <= 5) return { label: "This Week", urgent: false };
  return { label: format(pickup, "MMM d"), urgent: false };
}

function formatEquipment(type: string) {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(dateStr: string) {
  const diff = differenceInSeconds(new Date(), new Date(dateStr));
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return format(new Date(dateStr), "MMM d");
}

function getBidTrend(bids: LiveBid[]): { label: string; icon: typeof TrendingDown; color: string } {
  const pending = bids.filter(b => b.status === "pending");
  if (pending.length < 2) return { label: "Not enough data", icon: Minus, color: "text-muted-foreground" };
  const recent = pending.slice(0, Math.min(5, pending.length));
  const first = recent[recent.length - 1].bid_amount;
  const last = recent[0].bid_amount;
  if (last < first * 0.98) return { label: "Dropping", icon: TrendingDown, color: "text-emerald-500" };
  if (last > first * 1.02) return { label: "Rising", icon: TrendingUp, color: "text-destructive" };
  return { label: "Stable", icon: Minus, color: "text-muted-foreground" };
}

/* ── Signal Chips Component ── */
function BidSignalChips({ bid, size = "sm" }: { bid: LiveBid; size?: "sm" | "md" }) {
  const cls = size === "md" ? "text-[10px] px-2 py-0.5 h-5" : "text-[9px] px-1.5 py-0 h-4";
  return (
    <>
      {bid.ready_to_cover && (
        <Badge variant="outline" className={cn(cls, "border-primary/30 text-primary")}>
          Ready to Cover
        </Badge>
      )}
      {bid.truck_in_area && (
        <Badge variant="outline" className={cn(cls, "border-primary/30 text-primary")}>
          Truck in Area
        </Badge>
      )}
      {bid.team_available && (
        <Badge variant="outline" className={cn(cls, "border-primary/30 text-primary")}>
          Team Available
        </Badge>
      )}
      {bid.eta_hours != null && (
        <Badge variant="outline" className={cn(cls, "border-muted-foreground/30 text-muted-foreground")}>
          ETA {bid.eta_hours}h
        </Badge>
      )}
    </>
  );
}

function getSignalCount(bid: LiveBid): number {
  let c = 0;
  if (bid.ready_to_cover) c++;
  if (bid.truck_in_area) c++;
  if (bid.team_available) c++;
  return c;
}

/* ── Kayak-style Bid Card ── */
function brokerInitials(name: string | null | undefined) {
  const s = (name || "").trim();
  if (!s) return "B";
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "B") + (parts[1]?.[0] ?? "")).toUpperCase();
}

interface BidCardProps {
  bid: LiveBid;
  isLowest: boolean;
  isSelected: boolean;
  isAwarded: boolean;
  miles: number | null;
  avgBid: number | null;
  targetMax: number | null;
  awardsEnabled: boolean;
  onSelect: () => void;
  onAward: () => void;
  onMessage?: () => void;
  fmcsa?: { mc: string | null; dba: string | null; status_code: string | null; verified_at: string | null } | null;
}

function BidCard({
  bid, isLowest, isSelected, isAwarded, miles, avgBid, targetMax,
  awardsEnabled, onSelect, onAward, onMessage, fmcsa,
}: BidCardProps) {
  const { url: avatarUrl } = useAvatarUrl(bid.broker_avatar_path);
  const rpm = miles && miles > 0 ? bid.bid_amount / miles : null;

  // Comparison label — one meaningful line, real data only.
  let compare: { text: string; positive: boolean } | null = null;
  if (targetMax && targetMax > 0 && bid.bid_amount < targetMax) {
    compare = { text: `$${(targetMax - bid.bid_amount).toLocaleString()} below your budget`, positive: true };
  } else if (targetMax && targetMax > 0 && bid.bid_amount > targetMax) {
    compare = { text: `$${(bid.bid_amount - targetMax).toLocaleString()} above your budget`, positive: false };
  } else if (avgBid && Math.abs(bid.bid_amount - avgBid) / avgBid >= 0.02) {
    const pct = Math.round(((avgBid - bid.bid_amount) / avgBid) * 100);
    compare = pct > 0
      ? { text: `${pct}% below average`, positive: true }
      : { text: `${Math.abs(pct)}% above average`, positive: false };
  }

  return (
    <div
      className={cn(
        "relative rounded-lg border bg-card transition-shadow cursor-pointer",
        "hover:shadow-md",
        bid.isNew && "ring-2 ring-brand-teal/40 animate-in fade-in duration-500",
        isLowest
          ? "border-l-[3px] border-l-primary shadow-[0_1px_0_hsl(var(--primary)/0.15),0_8px_24px_-16px_hsl(var(--primary)/0.4)]"
          : "border-border",
        isSelected && !isLowest && "ring-1 ring-primary/40",
      )}
      onClick={() => { if (!isAwarded) onSelect(); }}
    >
      {/* Label chips (top-right) */}
      {isLowest && (
        <div className="absolute right-3 top-3">
          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium bg-primary/10 text-primary border-primary/40">
            <Zap className="h-3 w-3" />
            Lowest Bid
          </span>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-stretch gap-4 p-4">
        {/* LEFT — price block */}
        <div className="md:w-48 md:pr-4 md:border-r border-border shrink-0">
          <div className="text-3xl font-semibold leading-none tracking-tight tabular-nums text-foreground">
            ${bid.bid_amount.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-muted-foreground tabular-nums">
            {bid.rate_type === "per_mile" && rpm != null
              ? `Per Mile: $${rpm.toFixed(2)}/mi`
              : rpm != null
                ? `Flat Rate · $${rpm.toFixed(2)}/mi`
                : bid.rate_type === "per_mile" ? "Per Mile" : "Flat Rate"}
          </div>
          {compare && (
            <div className={cn("mt-2 text-xs font-medium", compare.positive ? "text-primary" : "text-muted-foreground")}>
              {compare.text}
            </div>
          )}
        </div>

        {/* RIGHT — broker + facts + actions */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {/* Broker row */}
          <div className="flex items-center gap-3 min-w-0 pr-24 sm:pr-28">
            <Avatar className="h-9 w-9">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt={bid.broker_company_name} /> : null}
              <AvatarFallback className="bg-muted text-foreground text-[11px] font-semibold">
                {brokerInitials(bid.broker_company_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-medium text-sm truncate">{bid.broker_company_name}</span>
                {bid.broker_company_verified && (
                  <span
                    className="inline-flex items-center text-primary shrink-0"
                    title="Verified on LaneRunner"
                    aria-label="Verified"
                  >
                    <BadgeCheck className="h-4 w-4" />
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground">Submitted {timeAgo(bid.submitted_at)}</div>
            </div>
          </div>

          {/* Signal chips (real data only) */}
          {(bid.ready_to_cover || bid.truck_in_area || bid.team_available || bid.eta_hours != null) && (
            <div className="flex flex-wrap gap-1">
              <BidSignalChips bid={bid} size="sm" />
            </div>
          )}

          {/* Message */}
          {bid.message && (
            <p className="text-xs text-muted-foreground italic line-clamp-2">"{bid.message}"</p>
          )}

          {/* Actions */}
          {((awardsEnabled && !isAwarded) || onMessage) && (
            <div className="flex items-center gap-2 pt-1 mt-auto">
              {awardsEnabled && !isAwarded && (
              <Button
                size="sm"
                variant={isSelected ? "default" : "outline"}
                className={cn(
                  "h-8 gap-1.5 text-xs",
                  isSelected
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "border-brand-teal/30 text-brand-teal hover:bg-brand-teal/10"
                )}
                onClick={(e) => { e.stopPropagation(); onAward(); }}
              >
                <Trophy className="h-3.5 w-3.5" />
                Award this bid
              </Button>
              )}
              {onMessage && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5 text-xs"
                  onClick={(e) => { e.stopPropagation(); onMessage(); }}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  Message
                </Button>
              )}
              <span className="min-w-0 truncate text-[11px] text-muted-foreground">
                {fmcsa?.mc ? (
                  <>
                    FMCSA: MC {String(fmcsa.mc).replace(/^MC[-\s]*/i, "")}
                    {fmcsa.status_code === "A" ? " · Active authority" : ""}
                    {fmcsa.dba && fmcsa.dba.trim().toLowerCase() !== bid.broker_company_name.trim().toLowerCase() ? ` · DBA ${fmcsa.dba}` : ""}
                    {fmcsa.verified_at ? ` · Verified ${new Date(fmcsa.verified_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                  </>
                ) : "FMCSA: Not currently available"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Component ── */
export default function LiveLoadRoomPage() {
  usePageMeta({ title: "LR Direct — Live Load Room | LaneRunner", description: "Watch brokers compete for your load in real time." });

  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { openChatWithUser } = useChatSystem();

  const [load, setLoad] = useState<DirectLoad | null>(null);
  // FMCSA-verified broker info per bid (shipper-only RPC; null = not available)
  const [bidFmcsa, setBidFmcsa] = useState<Record<string, { mc: string | null; dba: string | null; status_code: string | null; verified_at: string | null }>>({});
  const [stops, setStops] = useState<DirectStop[]>([]);
  const [bids, setBids] = useState<LiveBid[]>([]);
  const bidIdsKey = bids.map(b => b.id).sort().join(",");
  useEffect(() => {
    if (!id || !bidIdsKey) return;
    let cancelled = false;
    (supabase.rpc as any)("get_shipper_bid_fmcsa", { p_direct_load_id: id }).then(({ data }: any) => {
      if (cancelled || !data) return;
      setBidFmcsa(Object.fromEntries((data as any[]).map(r => [r.bid_id, r])));
    });
    return () => { cancelled = true; };
  }, [id, bidIdsKey]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedBidId, setSelectedBidId] = useState<string | null>(null);
  const [awardContact, setAwardContact] = useState<AwardContact | null>(null);
  const [contactUnavailable, setContactUnavailable] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [hasDispatch, setHasDispatch] = useState<boolean | null>(null);


  // Resolve total_miles at read-time when the post didn't save it (legacy /
  // racy posts). Also backfills the DB so bid cards / Atlas / analytics all
  // pick it up on next read.
  const { miles: resolvedMiles, loading: milesLoading } = useResolvedLoadMiles({
    loadId: load?.id,
    storedTotalMiles: load?.total_miles,
    originLat: load?.origin_latitude,
    originLng: load?.origin_longitude,
    destLat: load?.destination_latitude,
    destLng: load?.destination_longitude,
  });


  // Company name cache
  const companyCache = useRef<Record<string, string>>({});

  const resolveCompanyName = useCallback(async (companyId: string | null): Promise<string> => {
    if (!companyId) return "Unknown Broker";
    if (companyCache.current[companyId]) return companyCache.current[companyId];
    const { data } = await supabase.from("companies").select("name").eq("id", companyId).single();
    const name = (data as any)?.name || "Unknown Broker";
    companyCache.current[companyId] = name;
    return name;
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!id || !user) return;

    (async () => {
      setLoading(true);
      const { data: loadData, error: loadErr } = await supabase
        .from("direct_loads" as any).select("*").eq("id", id).single();
      if (loadErr || !loadData) { setLoading(false); return; }
      const ld = loadData as unknown as DirectLoad;

      // Owner gate
      if (ld.created_by !== user.id) {
        navigate("/lr-direct/my-opportunities", { replace: true });
        return;
      }
      setLoad(ld);

      // Parallel fetch stops + bids + broker responders (avatar + display name)
      const [stopsRes, bidsRes, respondersRes] = await Promise.all([
        supabase.from("direct_load_stops" as any)
          .select("id, stop_number, stop_type, name, address, city, state, postal_code, scheduled_date, scheduled_time_start, scheduled_time_end, instructions")
          .eq("direct_load_id", id).order("stop_number", { ascending: true }),
        supabase.from("direct_bids" as any)
          .select("id, bid_amount, message, status, submitted_at, broker_id, broker_company_id, ready_to_cover, truck_in_area, team_available, eta_hours, rate_type, withdraw_reason")
          .eq("direct_load_id", id).order("bid_amount", { ascending: true }),
        supabase.rpc("get_shipper_bid_responders", { p_direct_load_ids: [id] } as any),
      ]);

      if (stopsRes.data) setStops(stopsRes.data as unknown as DirectStop[]);

      if (bidsRes.data) {
        const rawBids = bidsRes.data as any[];
        const companyIds = [...new Set(rawBids.map(b => b.broker_company_id).filter(Boolean))];
        let companyMap: Record<string, { name: string; verified: boolean }> = {};
        if (companyIds.length > 0) {
          const { data: companies } = await supabase
            .from("companies")
            .select("id, name, verification_status")
            .in("id", companyIds);
          if (companies) {
            companyMap = Object.fromEntries(
              (companies as any[]).map(c => [c.id, { name: c.name, verified: c.verification_status === "verified" }])
            );
            Object.entries(companyMap).forEach(([k, v]) => (companyCache.current[k] = v.name));
          }
        }
        const avatarMap = new Map<string, string | null>();
        ((respondersRes.data as any[]) ?? []).forEach((r) => {
          avatarMap.set(r.broker_user_id, r.avatar_url ?? null);
        });
        setBids(rawBids.map(b => ({
          ...b,
          broker_company_name: displayCompanyName(companyMap[b.broker_company_id]?.name) || "Unknown Broker",
          broker_company_verified: companyMap[b.broker_company_id]?.verified ?? false,
          broker_avatar_path: avatarMap.get(b.broker_id) ?? null,
        })));
      }

      // If already awarded, fetch broker contact (shipper-facing RPC)
      if (ld.status === "awarded" && ld.awarded_bid_id) {
        const { data: contactData, error: contactErr } = await supabase.rpc(
          "get_direct_award_broker_contact",
          { p_load_id: id } as any,
        );
        if (contactErr) console.warn("award broker contact fetch failed", contactErr);
        const row = (contactData as any[])?.[0];
        if (row) {
          setAwardContact(row as AwardContact);
        } else {
          setContactUnavailable(true);
        }
      }

      // Does a dispatch already exist for this load? Drives the next-step prompt.
      const { data: dispRow } = await supabase
        .from("direct_dispatches" as any)
        .select("id")
        .eq("direct_load_id", id)
        .maybeSingle();
      setHasDispatch(!!dispRow);

      setLoading(false);
    })();
  }, [id, user, reloadTick]);

  // Refresh the dispatch flag when the shipper returns from the builder tab/window.
  useEffect(() => {
    if (!id) return;
    const onFocus = async () => {
      const { data } = await supabase
        .from("direct_dispatches" as any)
        .select("id")
        .eq("direct_load_id", id)
        .maybeSingle();
      setHasDispatch(!!data);
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [id]);


  // Default selectedBidId to lowest pending bid
  useEffect(() => {
    const pending = bids.filter(b => b.status === "pending");
    if (pending.length > 0 && (!selectedBidId || !pending.find(b => b.id === selectedBidId))) {
      setSelectedBidId(pending[0].id);
    }
  }, [bids]);

  // Realtime subscription
  useEffect(() => {
    if (!id || !user) return;
    const channel = supabase
      .channel(`live-room-bids-${id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "direct_bids",
        filter: `direct_load_id=eq.${id}`,
      }, async (payload) => {
        const newBid = payload.new as any;
        const companyName = await resolveCompanyName(newBid.broker_company_id);
        setBids(prev => {
          const mapped: LiveBid = { ...newBid, broker_company_name: companyName, isNew: true };
          const updated = [...prev.filter(b => b.id !== newBid.id), mapped];
          return updated.sort((a, b) => a.bid_amount - b.bid_amount);
        });
        setTimeout(() => {
          setBids(prev => prev.map(b => b.id === newBid.id ? { ...b, isNew: false } : b));
        }, 3000);
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "direct_bids",
        filter: `direct_load_id=eq.${id}`,
      }, (payload) => {
        const updated = payload.new as any;
        setBids(prev => prev.map(b => b.id === updated.id ? { ...b, status: updated.status, withdraw_reason: updated.withdraw_reason ?? (b as any).withdraw_reason } : b));
      })
      .subscribe((status) => {
        // Self-heal: refetch bids on (re)connect so events dropped during
        // a CHANNEL_ERROR gap don't leave the room stale.
        if (status === "SUBSCRIBED") setReloadTick((t) => t + 1);
      });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; setReloadTick((t) => t + 1); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [id, user, resolveCompanyName]);

  // Countdown timer
  useEffect(() => {
    if (!load?.bid_deadline) { setCountdown(""); return; }
    const tick = () => {
      const now = new Date();
      const deadline = new Date(load.bid_deadline!);
      const diff = differenceInSeconds(deadline, now);
      if (diff <= 0) { setCountdown("Closed"); return; }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      if (diff <= 600) setCountdown(`${m}m ${s}s`);
      else if (h > 0) setCountdown(`${h}h ${m}m`);
      else setCountdown(`${m}m`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [load?.bid_deadline]);

  // Award handler
  const handleAward = async () => {
    if (!selectedBidId) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("award_direct_bid", { p_bid_id: selectedBidId } as any);
    setSubmitting(false);
    setConfirmOpen(false);
    const result = data as any;
    if (error || !result?.success) {
      toast({ variant: "destructive", title: "Award failed", description: result?.error || error?.message });
      return;
    }
    toast({ title: "Load awarded!", description: "The winning broker has been notified." });
    setLoad(prev => prev ? { ...prev, status: "awarded", awarded_bid_id: selectedBidId } : prev);
    setBids(prev => prev.map(b => ({
      ...b,
      status: b.id === selectedBidId ? "accepted" : (b.status === "pending" ? "rejected" : b.status),
    })));
    // Fire-and-forget broker award email + SMS via unified notify pipeline.
    try {
      const loadAny = load as any;
      const winningBid: any = bids.find(b => b.id === selectedBidId);
      const lane = loadAny ? `${loadAny.origin_city}, ${loadAny.origin_state} → ${loadAny.destination_city}, ${loadAny.destination_state}` : "your lane";
      if (winningBid?.broker_id) {
        notifyClient({
          recipientUserId: winningBid.broker_id,
          eventType: "load_awarded",
          inAppType: "direct_load_awarded",
          title: `🎉 You Won the Bid!`,
          body: `${lane} — tap to start dispatch`,
          link: `/lr-direct/dispatches/won/${id}`,
          loadId: id,
          metadata: { direct_load_id: id, direct_bid_id: selectedBidId },
          skipInApp: true,
          smsBody: `LR Direct: You won ${loadAny?.load_number ?? "a load"} (${lane}). Open dispatch.`,
          emailPayload: { lane, load_number: loadAny?.load_number },
        });
      }
      // Losing brokers — bid_not_awarded
      bids.filter((b: any) => b.id !== selectedBidId && b.broker_id && b.status === "pending").forEach((b: any) => {
        notifyClient({
          recipientUserId: b.broker_id,
          eventType: "bid_not_awarded",
          inAppType: "direct_bid_not_awarded",
          title: `Bid closed on ${loadAny?.load_number ?? "load"}`,
          body: `The shipper selected a different broker for ${lane}. Keep an eye on the board for more loads.`,
          link: "/lr-direct/board",
          loadId: id,
          metadata: { direct_load_id: id, direct_bid_id: b.id },
          emailPayload: { lane, load_number: loadAny?.load_number },
        });
      });
    } catch (e) { console.warn("award notify failed", e); }

    // Fetch broker contact (shipper-facing RPC)
    const { data: contactData, error: contactErr } = await supabase.rpc(
      "get_direct_award_broker_contact",
      { p_load_id: id } as any,
    );
    if (contactErr) console.warn("award broker contact fetch failed", contactErr);
    const row = (contactData as any[])?.[0];
    if (row) {
      setAwardContact(row as AwardContact);
      setContactUnavailable(false);
    } else {
      setContactUnavailable(true);
    }
  };


  // Derived
  const pendingBids = useMemo(() => bids.filter(b => b.status === "pending"), [bids]);
  const lowestBid = pendingBids[0] || null;
  const highestBid = pendingBids.length > 0 ? pendingBids[pendingBids.length - 1] : null;
  const avgBid = pendingBids.length > 0 ? Math.round(pendingBids.reduce((s, b) => s + b.bid_amount, 0) / pendingBids.length) : null;
  const slotsRemaining = load ? Math.max(0, load.max_bidders - pendingBids.length) : 0;
  const slotPercent = load ? ((pendingBids.length / load.max_bidders) * 100) : 0;
  const trend = getBidTrend(pendingBids);
  const isAwarded = load?.status === "awarded";
  const isClosed = countdown === "Closed" || isAwarded;
  const pickupChip = load ? getPickupChip(load.pickup_date) : null;

  const selectedBid = selectedBidId ? bids.find(b => b.id === selectedBidId) : null;
  const winningBid = isAwarded ? bids.find(b => b.status === "accepted") : null;

  // Status bar state
  const statusLabel = isAwarded ? "Awarded" : isClosed ? "Bidding Closed" : "Bidding Live";
  const deadlineSeconds = load?.bid_deadline
    ? Math.max(0, differenceInSeconds(new Date(load.bid_deadline), new Date()))
    : Infinity;
  const isUrgentCountdown = deadlineSeconds <= 1800;
  const isCriticalCountdown = deadlineSeconds <= 600;

  // Velocity signal
  const recentBidCount = useMemo(() => {
    const tenMinAgo = new Date(Date.now() - 10 * 60_000);
    return pendingBids.filter(b => new Date(b.submitted_at) > tenMinAgo).length;
  }, [pendingBids]);

  const momentumLabel = recentBidCount >= 3 ? "Heating Up 🔥" : recentBidCount >= 1 ? "Active" : null;

  // Confidence for recommended winner
  const recommendedConfidence = useMemo(() => {
    if (!lowestBid) return null;
    const signals = getSignalCount(lowestBid);
    if (signals >= 2) return "High Confidence";
    if (signals >= 1) return "Good Match";
    return "Based on Price";
  }, [lowestBid]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!load) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3">
        <AlertTriangle className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground font-medium">Load not found</p>
        <Button variant="outline" onClick={() => navigate("/lr-direct/my-opportunities")}>Go Back</Button>
      </div>
    );
  }

  const confirmingBid = selectedBid;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── STATUS BAR ── */}
      <div className={cn(
        "border-b px-3 sm:px-6 py-3 flex items-center justify-between gap-3 sm:gap-4 flex-wrap",
        isAwarded
          ? "bg-brand-teal/10 border-brand-teal/20"
          : isClosed
            ? "bg-muted border-border"
            : isCriticalCountdown
              ? "bg-destructive/10 border-destructive/20"
              : "bg-gradient-to-r from-brand-teal/10 to-brand-teal/5 border-brand-teal/20"
      )}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/lr-direct/my-loads")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            {!isClosed && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-destructive" />
              </span>
            )}
            {isAwarded && <CheckCircle2 className="h-4 w-4 text-brand-teal" />}
            <span className={cn(
              "font-bold text-sm",
              isAwarded ? "text-brand-teal" : isClosed ? "text-muted-foreground" : "text-foreground"
            )}>
              {statusLabel}
            </span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{load.load_number}</span>
        </div>

        <div className="flex items-center gap-5 text-sm">
          {momentumLabel && !isAwarded && (
            <Badge className={cn(
              "text-[10px] font-bold border-0",
              recentBidCount >= 3 ? "bg-orange-500/10 text-orange-500" : "bg-brand-teal/10 text-brand-teal"
            )}>
              {momentumLabel}
            </Badge>
          )}
          {!isAwarded && load.bid_deadline && (
            <div className={cn(
              "flex items-center gap-1.5 font-semibold",
              isCriticalCountdown ? "text-destructive" : isUrgentCountdown ? "text-warning" : "text-foreground"
            )}>
              <Clock className="h-3.5 w-3.5" />
              {countdown === "Closed" ? "Closed" : `Closes in ${countdown}`}
            </div>
          )}
          {!isAwarded && (
            <>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <CircleDot className="h-3.5 w-3.5" />
                <span className="font-medium">{pendingBids.length} bidding</span>
              </div>
              <div className="text-muted-foreground">
                {slotsRemaining} slot{slotsRemaining !== 1 ? "s" : ""} left
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── NEXT-STEP PROMPT — awarded but no dispatch yet ── */}
      {isAwarded && hasDispatch === false && (
        <div className="px-4 pt-4">
          <div className="rounded-lg border-2 border-lr-yellow bg-lr-yellow/25 px-4 py-3.5 flex items-start justify-between gap-4 flex-wrap shadow-sm">
            <div className="flex items-start gap-3 min-w-0">
              <ClipboardList className="h-5 w-5 text-foreground flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-bold text-sm text-foreground">Next step: Create the dispatch</p>
                <p className="text-xs text-foreground/80 mt-0.5">
                  Your broker needs the pickup details, contacts, reference numbers, and instructions before they can run this load.
                </p>
              </div>
            </div>
            <Button
              className="gap-2 text-xs flex-shrink-0"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              onClick={() => navigate(`/lr-direct/dispatches/build/${load.id}`)}
            >
              <ClipboardList className="h-3.5 w-3.5" />
              Create Dispatch
            </Button>
          </div>
        </div>
      )}



      {/* ── 3-COLUMN GRID ── */}
      <div className="flex-1 px-3 sm:px-4 py-4 grid gap-4 items-start grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)_320px]">

        {/* ── LEFT COLUMN ── */}
        <div className="space-y-3 min-w-0 order-2 lg:order-1">
          {/* Load Overview — lane-first hierarchy with teal accent */}
          <div className="border border-border rounded-lg bg-card overflow-hidden">
            {/* Accent header */}
            <div className="h-1 bg-gradient-to-r from-brand-teal to-[#1AB7AA]" />
            <div className="p-3.5 space-y-3">
              {/* Load number + tags */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono text-muted-foreground tracking-wider">
                  {load.load_number}
                </span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {load.priority_tag === "priority" && (
                    <Badge variant="destructive" className="text-[9px] h-4 px-1.5">⚡ Priority</Badge>
                  )}
                  {load.load_type && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1.5 uppercase">{load.load_type}</Badge>
                  )}
                  {load.appointment_type && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1.5 capitalize">
                      {load.appointment_type.replace(/_/g, " ")}
                    </Badge>
                  )}
                </div>
              </div>

              {load.reference_number && (
                <p className="text-[11px] text-muted-foreground -mt-1">
                  Ref <span className="text-foreground font-medium">{load.reference_number}</span>
                </p>
              )}

              {/* Lane — prominent */}
              <div>
                <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  {stops.length > 2 ? `Route · ${stops.length} stops` : "Lane"}
                </h3>
                {stops.length > 0 ? (
                  <ol className="space-y-2">
                    {stops.map((s, idx) => {
                      const isPick = s.stop_type === "pickup";
                      const timeLabel = s.scheduled_time_start
                        ? (s.scheduled_time_end && s.scheduled_time_end !== s.scheduled_time_start
                            ? `${s.scheduled_time_start.slice(0,5)}–${s.scheduled_time_end.slice(0,5)}`
                            : s.scheduled_time_start.slice(0,5))
                        : null;
                      return (
                        <li key={s.id} className="flex items-start gap-2">
                          <div className={cn(
                            "mt-1 h-2 w-2 rounded-full flex-shrink-0 ring-2 ring-offset-1 ring-offset-card",
                            isPick ? "bg-brand-teal ring-brand-teal/30" : "bg-destructive ring-destructive/30",
                          )} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">
                                {isPick ? "Pick" : "Drop"} {idx + 1}
                              </span>
                              <span className="font-semibold text-sm text-foreground leading-tight">
                                {s.city}, {s.state}
                              </span>
                            </div>
                            {s.name && (
                              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{s.name}</p>
                            )}
                            <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5">
                              {format(new Date(s.scheduled_date + "T00:00:00"), "EEE, MMM d")}
                              {timeLabel && <span> · {timeLabel}</span>}
                            </p>
                            {s.instructions && (
                              <p className="text-[11px] italic text-muted-foreground mt-0.5">"{s.instructions}"</p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-brand-teal ring-2 ring-brand-teal/30 flex-shrink-0" />
                      <span className="font-semibold text-sm text-foreground">
                        {load.origin_city}, {load.origin_state}
                      </span>
                    </div>
                    <div className="ml-[3px] border-l-2 border-dashed border-border h-3" />
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-destructive ring-2 ring-destructive/30 flex-shrink-0" />
                      <span className="font-semibold text-sm text-foreground">
                        {load.destination_city}, {load.destination_state}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-2 mt-1 border-t border-border/60">
                      <div>
                        <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Pickup</div>
                        <div className="text-xs font-medium text-foreground tabular-nums">
                          {format(new Date(load.pickup_date + "T00:00:00"), "MMM d")}
                        </div>
                      </div>
                      {load.delivery_date && (
                        <div>
                          <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Delivery</div>
                          <div className="text-xs font-medium text-foreground tabular-nums">
                            {format(new Date(load.delivery_date + "T00:00:00"), "MMM d")}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Miles chip (resolved from stored or computed via Mapbox) */}
                {resolvedMiles ? (
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-teal/10 border border-brand-teal/20 px-2 py-1">
                    <Route className="h-3 w-3 text-brand-teal" />
                    <span className="text-[11px] font-semibold text-brand-teal tabular-nums">
                      {resolvedMiles.toLocaleString()} mi
                    </span>
                  </div>
                ) : milesLoading ? (
                  <div className="mt-3 text-[10px] text-muted-foreground italic">Computing lane distance…</div>
                ) : null}

                {pickupChip && (
                  <div className="mt-2">
                    <Badge variant={pickupChip.urgent ? "destructive" : "outline"} className="text-[10px]">
                      {pickupChip.label}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Equipment strip */}
              <div className="pt-3 border-t border-border/60">
                <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Equipment</h3>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="h-7 w-7 rounded-md bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                    <Truck className="h-4 w-4 text-brand-teal" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground leading-tight">
                      {formatEquipment(load.equipment_type)}
                    </div>
                    {(load.weight_lbs || load.length_ft) && (
                      <div className="text-[11px] text-muted-foreground tabular-nums">
                        {load.weight_lbs ? `${load.weight_lbs.toLocaleString()} lbs` : ""}
                        {load.weight_lbs && load.length_ft ? " · " : ""}
                        {load.length_ft ? `${load.length_ft} ft` : ""}
                      </div>
                    )}
                  </div>
                </div>
                {load.commodity && (
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Package className="h-3 w-3" />
                    {load.commodity}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Transit Time Predictor — sits directly under equipment on the left */}
          <TransitTimePredictorWidget
            loadId={load.id}
            origin={`${load.origin_city}, ${load.origin_state}`}
            destination={`${load.destination_city}, ${load.destination_state}`}
            miles={resolvedMiles}
            defaultPickupDate={load.pickup_date ?? null}
          />



          {/* Handling Requirements */}
          {load.handling_requirements && load.handling_requirements.length > 0 && (
            <div className="border border-border rounded-lg bg-card p-3">
              <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Handling</h3>
              <div className="flex flex-wrap gap-1">
                {load.handling_requirements.map((h) => (
                  <Badge key={h} variant="outline" className="text-[10px] capitalize">
                    {h.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Special Instructions (includes parsed pick/drop hours) */}
          {load.special_instructions && (
            <div className="border border-border rounded-lg bg-card p-3">
              <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <FileText className="h-3 w-3" /> Instructions
              </h3>
              <p className="text-xs text-foreground leading-snug whitespace-pre-line">
                {load.special_instructions}
              </p>
            </div>
          )}

          {/* Shipper notes to brokers */}
          {load.shipper_notes_to_brokers && (
            <div className="border border-border rounded-lg bg-card p-3">
              <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <StickyNote className="h-3 w-3" /> Notes to Brokers
              </h3>
              <p className="text-xs text-foreground leading-snug whitespace-pre-line">
                {load.shipper_notes_to_brokers}
              </p>
            </div>
          )}

          {/* Target Rate Card */}
          {(load.target_rate_min || load.target_rate_max) && (
            <div className="border border-border rounded-lg bg-card p-3">
              <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Your Budget</h3>
              <p className="text-lg font-bold text-foreground tabular-nums">
                {load.target_rate_min && load.target_rate_max
                  ? `$${load.target_rate_min.toLocaleString()} – $${load.target_rate_max.toLocaleString()}`
                  : load.target_rate_max
                    ? `Up to $${load.target_rate_max.toLocaleString()}`
                    : `From $${load.target_rate_min!.toLocaleString()}`}
              </p>
              {load.total_miles && load.target_rate_max && (
                <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                  ${(load.target_rate_max / load.total_miles).toFixed(2)}/mi budget
                </p>
              )}
            </div>
          )}
        </div>


        {/* ── MIDDLE COLUMN — BID FEED (live) or BID HISTORY (awarded) ── */}
        {/* Mobile: the bid feed is the primary content, so it renders FIRST. */}
        <div className="space-y-3 min-w-0 order-1 lg:order-2">
          {isAwarded ? (
            <div className="border border-border rounded-lg bg-card">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-sm text-foreground">Bids Received</h3>
                <span className="text-[10px] text-muted-foreground">
                  {bids.filter(b => b.status !== "withdrawn").length} total
                </span>
              </div>
              <div className="p-2 space-y-2">
                {/* Winner pinned */}
                {winningBid && (
                  <div className="border-2 border-brand-teal/40 bg-brand-teal/5 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[10px] font-bold gap-1">
                        <Trophy className="h-3 w-3" />
                        Winner
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{timeAgo(winningBid.submitted_at)}</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground tabular-nums">
                      ${winningBid.bid_amount.toLocaleString()}
                    </p>
                    {load.total_miles && (
                      <p className="text-[10px] text-muted-foreground tabular-nums">
                        ${(winningBid.bid_amount / load.total_miles).toFixed(2)}/mi
                      </p>
                    )}
                    <p className="text-sm font-medium text-foreground mt-1">
                      {winningBid.broker_company_name}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <BidSignalChips bid={winningBid} size="md" />
                    </div>
                  </div>
                )}

                {/* All other bids collapsed as history */}
                {bids.filter(b => b.id !== winningBid?.id).length === 0 ? (
                  <p className="px-3 py-4 text-[11px] text-muted-foreground text-center">
                    No other bids were received.
                  </p>
                ) : (
                  <div className="space-y-1 px-1 pt-1">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider px-2 mb-1">
                      Other bids
                    </p>
                    {bids
                      .filter(b => b.id !== winningBid?.id)
                      .sort((a, b) => a.bid_amount - b.bid_amount)
                      .map(bid => (
                        <div
                          key={bid.id}
                          className="flex items-center justify-between text-xs text-muted-foreground px-2 py-1.5 rounded hover:bg-muted/40"
                        >
                          <span className="truncate mr-2">{bid.broker_company_name}</span>
                          <span className="tabular-nums shrink-0">
                            ${bid.bid_amount.toLocaleString()}
                            {load.total_miles && (
                              <span className="text-muted-foreground/60 ml-1.5">
                                ${(bid.bid_amount / load.total_miles).toFixed(2)}/mi
                              </span>
                            )}
                            <span className="text-muted-foreground/60 ml-1.5">· {bid.status}</span>
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="border border-border rounded-lg bg-card">
                {/* Feed Header */}
                <div className="px-4 py-3 border-b border-border">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-sm text-foreground">Live Bid Feed</h3>
                    <div className="flex items-center gap-2">
                      {recentBidCount > 0 && (
                        <span className="text-[10px] font-medium text-muted-foreground">
                          {recentBidCount} bid{recentBidCount !== 1 ? "s" : ""} in last 10min
                        </span>
                      )}
                      <div className={cn("flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full",
                        trend.color,
                        trend.label === "Dropping" ? "bg-emerald-500/10" : trend.label === "Rising" ? "bg-destructive/10" : "bg-muted"
                      )}>
                        <trend.icon className="h-3 w-3" />
                        {trend.label}
                      </div>
                    </div>
                  </div>
                  {/* Spread summary */}
                  {pendingBids.length >= 2 && lowestBid && highestBid && avgBid && (
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span>Low <strong className="text-foreground">${lowestBid.bid_amount.toLocaleString()}</strong></span>
                      <span>Avg <strong className="text-foreground">${avgBid.toLocaleString()}</strong></span>
                      <span>High <strong className="text-foreground">${highestBid.bid_amount.toLocaleString()}</strong></span>
                    </div>
                  )}
                </div>

                {/* Bid Cards */}
                <div className="p-2 space-y-2">
                  {pendingBids.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <div className="relative mx-auto w-12 h-12 mb-3">
                        <Radio className="h-12 w-12 text-brand-teal/20" />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-50" />
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-teal" />
                          </span>
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground">Your load is live</p>
                      <p className="text-xs text-muted-foreground mt-1">Brokers are being notified — bids will appear here as they come in</p>
                    </div>
                  ) : (
                    pendingBids.map((bid, i) => {
                      const isLowest = i === 0;
                      const isSelected = bid.id === selectedBidId && !isAwarded;
                      return (
                        <BidCard
                          key={bid.id}
                          bid={bid}
                          isLowest={isLowest}
                          isSelected={isSelected}
                          isAwarded={isAwarded}
                          miles={load?.total_miles ?? null}
                          avgBid={avgBid}
                          targetMax={load?.target_rate_max ?? null}
                          awardsEnabled={AWARDS_ENABLED}
                          onSelect={() => setSelectedBidId(bid.id)}
                          onAward={() => { setSelectedBidId(bid.id); setConfirmOpen(true); }}
                          onMessage={bid.broker_id ? () => openChatWithUser(bid.broker_id, bid.broker_company_name, undefined, load.id) : undefined}
                          fmcsa={bidFmcsa[bid.id] ?? null}
                        />
                      );
                    })
                  )}
                </div>

                {/* Slots remaining bar */}
                {load.max_bidders > 0 && pendingBids.length > 0 && (
                  <div className="px-4 py-3 border-t border-border">
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1.5">
                      <span>{pendingBids.length} / {load.max_bidders} slots filled</span>
                      <span>{slotsRemaining} remaining</span>
                    </div>
                    <Progress value={slotPercent} className="h-1.5" />
                  </div>
                )}
              </div>

              {/* Retracted / inactive bids — shown for transparency, never awardable */}
              {bids.filter(b => b.status !== "pending" && b.status !== "accepted").length > 0 && (
                <div className="border border-border rounded-lg bg-card p-3">
                  <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                    Retracted &amp; inactive bids
                  </h4>
                  <div className="space-y-1">
                    {bids.filter(b => b.status !== "pending" && b.status !== "accepted").map(bid => {
                      const retracted = bid.status === "withdrawn";
                      return (
                        <div key={bid.id} className="text-xs text-muted-foreground opacity-70">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{bid.broker_company_name}</span>
                            <span className="flex shrink-0 items-center gap-1.5">
                              <span className={retracted ? "line-through" : ""}>
                                ${bid.bid_amount.toLocaleString()}
                              </span>
                              <span className="rounded border border-dashed border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                                {retracted ? "Retracted" : bid.status === "rejected" ? "Not selected" : bid.status}
                              </span>
                            </span>
                          </div>
                          {retracted && (bid as any).withdraw_reason && (
                            <p className="mt-0.5 text-[11px] italic">Reason: {(bid as any).withdraw_reason}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── RIGHT COLUMN — AWARD DECISION ── */}
        <div className="space-y-3 min-w-0 order-3 lg:col-span-2 xl:col-span-1">
          {isAwarded ? (
            <div className="space-y-3 animate-in fade-in duration-300">
              {/* Success Card */}
              <div className="border-2 border-brand-teal rounded-lg bg-brand-teal/5 p-5">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="h-10 w-10 rounded-full bg-brand-teal/10 flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-brand-teal" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-base">Load Awarded</h3>
                    <p className="text-xs text-muted-foreground">You've selected the winning broker.</p>
                  </div>
                </div>

                {(winningBid || awardContact) && (
                  <div className="bg-card rounded-lg border border-border p-3 space-y-1">
                    <p className="text-sm font-semibold text-foreground">
                      {displayCompanyName(awardContact?.company_name) || winningBid?.broker_company_name}
                    </p>
                    {winningBid && (
                      <p className="text-2xl font-bold text-brand-teal tabular-nums">
                        ${winningBid.bid_amount.toLocaleString()}
                      </p>
                    )}
                    {winningBid && load.total_miles && (
                      <p className="text-xs text-muted-foreground tabular-nums">
                        ${(winningBid.bid_amount / load.total_miles).toFixed(2)}/mi
                      </p>
                    )}
                    {winningBid && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        <BidSignalChips bid={winningBid} size="md" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Reveal Card */}
              {awardContact ? (
                <div className="border border-border rounded-lg bg-card p-4 space-y-3">
                  <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Broker Contact</h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-[10px] text-muted-foreground">Company</p>
                      <p className="text-sm font-semibold text-foreground">{displayCompanyName(awardContact.company_name)}</p>
                    </div>
                    {awardContact.contact_name && (
                      <div>
                        <p className="text-[10px] text-muted-foreground">Contact</p>
                        <p className="text-sm font-medium text-foreground">{awardContact.contact_name}</p>
                      </div>
                    )}
                    {awardContact.phone && (
                      <div>
                        <p className="text-[10px] text-muted-foreground">Phone</p>
                        <p className="text-sm font-medium text-foreground">{formatPhoneNumber(awardContact.phone)}</p>
                      </div>
                    )}
                    {awardContact.email && (
                      <div>
                        <p className="text-[10px] text-muted-foreground">Email</p>
                        <p className="text-sm font-medium text-foreground">{awardContact.email}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {winningBid?.broker_id && (
                      <Button
                        className="w-full gap-1.5 text-xs"
                        style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                        onClick={() => openChatWithUser(
                          winningBid.broker_id,
                          awardContact.contact_name || displayCompanyName(awardContact.company_name),
                          undefined,
                          load.id,
                        )}
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Message Broker
                      </Button>
                    )}
                    {awardContact.email && (
                      <Button
                        variant="outline"
                        className="w-full gap-1.5 text-xs"
                        onClick={() => window.location.href = `mailto:${awardContact.email}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Email Broker
                      </Button>
                    )}
                  </div>
                </div>
              ) : contactUnavailable ? (
                <div className="border border-border rounded-lg bg-card p-4 text-center space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Contact details unavailable — message the broker directly.
                  </p>
                  {winningBid?.broker_id && (
                    <Button
                      className="w-full gap-1.5 text-xs"
                      style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                      onClick={() => openChatWithUser(
                        winningBid.broker_id,
                        winningBid.broker_company_name,
                        undefined,
                        load.id,
                      )}
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      Message Broker
                    </Button>
                  )}
                </div>
              ) : (
                <div className="border border-border rounded-lg bg-card p-4 text-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Loading contact details...</p>
                </div>
              )}

              {/* Next Steps */}
              <div className="border border-border rounded-lg bg-card p-4 space-y-2">
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">
                  Next Steps
                </h4>
                <Button
                  className="w-full justify-start gap-2 text-xs h-9"
                  style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                  onClick={() => navigate(`/lr-direct/dispatches/build/${load.id}`)}
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Build Dispatch
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 text-xs h-9"
                  onClick={() => navigate(`/lr-direct/load/${load.id}`)}
                >
                  <Navigation2 className="h-3.5 w-3.5" />
                  Track Load
                </Button>
                {winningBid?.broker_id && (
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2 text-xs h-9"
                    onClick={() => openChatWithUser(
                      winningBid.broker_id,
                      awardContact?.contact_name || winningBid.broker_company_name,
                      undefined,
                      load.id,
                    )}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Message Broker
                  </Button>
                )}
              </div>

              <Button
                variant="ghost"
                className="w-full text-xs text-muted-foreground"
                onClick={() => navigate("/lr-direct/my-loads")}
              >
                Back to My Loads
              </Button>
            </div>
          ) : (
            <>
              {/* Decision Panel — quote-only mode hides the Award CTA; flip AWARDS_ENABLED to restore. */}
              {AWARDS_ENABLED ? (
              <div className="border-2 border-brand-teal/30 rounded-lg bg-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-foreground">Award Decision</h3>
                  {selectedBid && selectedBid.id === lowestBid?.id && recommendedConfidence && (
                    <Badge className="bg-brand-teal/10 text-brand-teal border-0 text-[9px] font-bold gap-1">
                      <Shield className="h-3 w-3" />
                      {recommendedConfidence}
                    </Badge>
                  )}
                </div>
                {selectedBid ? (
                  <div className="space-y-3">
                    {selectedBid.id === lowestBid?.id && (
                      <p className="text-[10px] text-brand-teal font-semibold uppercase tracking-wider">Recommended Winner</p>
                    )}
                    <div className={cn(
                      "rounded-lg p-3 border transition-all duration-200",
                      selectedBid.id === lowestBid?.id
                        ? "bg-emerald-500/5 border-emerald-500/20"
                        : "bg-primary/5 border-primary/20"
                    )}>
                      <div className="flex items-center justify-between mb-1">
                        {selectedBid.id === lowestBid?.id ? (
                          <Badge className="bg-emerald-500/10 text-emerald-600 border-0 text-[10px] font-bold">Lowest Bid</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Selected</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground">{timeAgo(selectedBid.submitted_at)}</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground tabular-nums">
                        ${selectedBid.bid_amount.toLocaleString()}
                      </p>
                      {load.total_miles && (
                        <p className="text-xs text-muted-foreground tabular-nums">
                          ${(selectedBid.bid_amount / load.total_miles).toFixed(2)}/mi
                        </p>
                      )}
                      <p className="text-sm font-medium text-foreground mt-1">{selectedBid.broker_company_name}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <BidSignalChips bid={selectedBid} size="md" />
                      </div>
                    </div>

                    <Button
                      className="w-full h-11 font-bold text-sm gap-2"
                      style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                      onClick={() => setConfirmOpen(true)}
                    >
                      <Trophy className="h-4 w-4" />
                      AWARD LOAD
                    </Button>

                    <p className="text-[10px] text-muted-foreground text-center">
                      Awarding reveals your contact info to the winning broker only
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="relative mx-auto w-10 h-10 mb-3">
                      <Radio className="h-10 w-10 text-muted-foreground/20" />
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-50" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" />
                        </span>
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground font-medium">Waiting for bids</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">Select a bid from the feed to award</p>
                  </div>
                )}
              </div>
              ) : (
                <div className="border-2 border-brand-teal/30 rounded-lg bg-card p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-brand-teal" />
                    <h3 className="font-bold text-sm text-foreground">Quotes Only</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Awarding is paused while we finalize this flow. Review the broker quotes on the left — you'll receive an email for every new quote as it comes in.
                  </p>
                </div>
              )}


              {/* Atlas award analysis (honest v1) */}
              <AtlasAwardAnalysisPanel
                loadId={load.id}
                bidsCount={bids.length}
                bidsFingerprint={bids.map(b => `${b.id}:${b.bid_amount}:${b.status}`).sort().join("|")}
                awarded={!!load.awarded_bid_id}
              />

              {/* NOTE: LanePredictor moved to the left column (under Equipment). */}



              {/* Market Insights (credit-gated) */}
              <MarketInsightsPanel load={load} bids={bids} />
            </>
          )}
        </div>
      </div>

      {/* ── AWARD CONFIRMATION DIALOG ── */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Trophy className="h-5 w-5 text-brand-teal" />
              Confirm Award
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              You are about to award this load to:
            </DialogDescription>
          </DialogHeader>

          {confirmingBid && (
            <div className="space-y-4 py-2">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-foreground mb-1">{confirmingBid.broker_company_name}</p>
                <p className="text-3xl font-bold text-brand-teal tabular-nums">
                  ${confirmingBid.bid_amount.toLocaleString()}
                </p>
                {load.total_miles && (
                  <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                    ${(confirmingBid.bid_amount / load.total_miles).toFixed(2)}/mi
                  </p>
                )}
                <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                  <BidSignalChips bid={confirmingBid} size="md" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                You will be connected directly after confirmation. All other bids will be rejected.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleAward}
              disabled={submitting}
              className="gap-2 font-semibold"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                  Awarding...
                </>
              ) : (
                "Confirm Award"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
